// Pure serialization for clip-builder overlays (speech bubbles, title
// overlays, logo overlays, subscribe buttons).
//
// Converts the raw string/boolean values read off an overlay form into the
// JSON payload the update endpoint expects (seconds -> frames, defaults,
// constant fields), and the inverse (`toFormValues`) for pre-filling the edit
// form. Also owns the cross-clip clipboard envelope shape. No DOM, no
// Stimulus — every branch is unit-tested.

export const FPS = 30

// Overlay "kind" -> the config_data field it writes to.
export const OVERLAY_KIND_TO_FIELD = {
  bubble: "speech_bubbles",
  title: "title_overlays",
  logo: "logo_overlays",
  subscribe: "subscribe_buttons",
}

// Matches the original controller's `parseFloat(x) || fallback` semantics:
// a literal "0" (falsy) collapses to the fallback. Behaviour-preserving.
function secondsToFrames(value, fallback) {
  const secs = parseFloat(value) || fallback
  return Math.round(secs * FPS)
}

function optionalSecondsToFrames(value) {
  if (value === "" || value === null || value === undefined) return null
  const secs = parseFloat(value)
  return Number.isNaN(secs) ? null : Math.round(secs * FPS)
}

// parseInt with no fallback — number inputs always carry a value, and NaN
// (should it ever occur) serialises to null exactly as before.
function int(value) {
  return parseInt(value, 10)
}

// Serialize a raw form read into an overlay payload.
// `carried` holds fields preserved across an edit that the form does not
// expose (currently only the speech bubble's start_frame / draw_duration).
export function serializeOverlay(kind, raw, carried = {}) {
  switch (kind) {
    case "bubble":
      return {
        text: raw.text,
        x_pct: int(raw.x_pct),
        y_pct: int(raw.y_pct),
        width_pct: int(raw.width_pct),
        font_size: int(raw.font_size),
        tail_direction: raw.tail_direction,
        start_frame: carried.start_frame ?? 10,
        draw_duration: carried.draw_duration ?? 20,
      }
    case "title":
      return {
        text: raw.text,
        x_pct: int(raw.x_pct),
        y_pct: int(raw.y_pct),
        font_size: int(raw.font_size),
        font_weight: int(raw.font_weight),
        font_family: raw.font_family,
        color: raw.color,
        text_align: raw.text_align || "center",
        animation: raw.animation,
        exit_animation: raw.exit_animation,
        text_outline: !!raw.text_outline,
        show_background: !!raw.show_background,
        start_frame: secondsToFrames(raw.start_seconds, 0.2),
        end_frame: optionalSecondsToFrames(raw.end_seconds),
        entrance_duration: 15,
      }
    case "logo":
      return {
        src: raw.src,
        asset_id: raw.asset_id ?? null,
        asset_name: raw.asset_name,
        x_pct: int(raw.x_pct),
        y_pct: int(raw.y_pct),
        width: int(raw.width),
        height: int(raw.height),
        border_radius: int(raw.border_radius),
        animation: raw.animation,
        shadow: !!raw.shadow,
        object_fit: raw.object_fit,
        start_frame: secondsToFrames(raw.start_seconds, 0),
        end_frame: optionalSecondsToFrames(raw.end_seconds),
        entrance_duration: 15,
      }
    case "subscribe":
      return {
        preset: raw.preset,
        text: raw.text || null,
        subtitle: raw.subtitle || null,
        icon: raw.icon || null,
        x_pct: int(raw.x_pct),
        y_pct: int(raw.y_pct),
        animation: raw.animation,
        primary_color: raw.primary_color,
        pulse_after_entrance: !!raw.pulse_after_entrance,
        shadow: !!raw.shadow,
        start_frame: secondsToFrames(raw.start_seconds, 0.5),
        end_frame: optionalSecondsToFrames(raw.end_seconds),
        entrance_duration: 20,
      }
    default:
      return null
  }
}

// Inverse of serialize: turn a stored overlay into the field values used to
// pre-fill the edit form. Returns a { dataField: value } map (strings for
// text/number/select inputs, booleans for checkboxes). Values not listed here
// (logo src / asset id / asset name) are populated directly by the caller.
export function toFormValues(kind, overlay = {}) {
  const p = overlay || {}
  const seconds = (frame, fallback) => (frame ? (frame / FPS).toFixed(1) : fallback)

  switch (kind) {
    case "bubble":
      return {
        text: p.text ?? "",
        x_pct: p.x_pct ?? 50,
        y_pct: p.y_pct ?? 30,
        width_pct: p.width_pct ?? 25,
        font_size: p.font_size ?? 32,
        tail_direction: p.tail_direction ?? "bottom-left",
      }
    case "title":
      return {
        text: p.text ?? "",
        font_family: p.font_family ?? "Inter",
        font_weight: String(p.font_weight ?? 700),
        font_size: p.font_size ?? 48,
        color: p.color ?? "#FFFFFF",
        text_align: p.text_align ?? "center",
        x_pct: p.x_pct ?? 50,
        y_pct: p.y_pct ?? 50,
        start_seconds: seconds(p.start_frame, "0.2"),
        end_seconds: seconds(p.end_frame, ""),
        animation: p.animation ?? "fade",
        exit_animation: p.exit_animation ?? "none",
        text_outline: !!p.text_outline,
        show_background: !!p.show_background,
      }
    case "logo":
      return {
        x_pct: p.x_pct ?? 50,
        y_pct: p.y_pct ?? 30,
        width: p.width ?? 200,
        height: p.height ?? 200,
        border_radius: p.border_radius ?? 0,
        animation: p.animation ?? "scale",
        start_seconds: seconds(p.start_frame, "0"),
        end_seconds: seconds(p.end_frame, ""),
        shadow: !!p.shadow,
        object_fit: p.object_fit ?? "contain",
      }
    case "subscribe":
      return {
        preset: p.preset ?? "website",
        text: p.text ?? "",
        subtitle: p.subtitle ?? "",
        icon: p.icon ?? "",
        x_pct: p.x_pct ?? 50,
        y_pct: p.y_pct ?? 60,
        start_seconds: seconds(p.start_frame, "0.5"),
        end_seconds: seconds(p.end_frame, ""),
        animation: p.animation ?? "scale-bounce",
        primary_color: p.primary_color ?? "#6366F1",
        pulse_after_entrance: !!p.pulse_after_entrance,
        shadow: p.shadow !== false,
      }
    default:
      return {}
  }
}

// Cross-clip / cross-tab clipboard envelope. `index` is a per-render UI
// artefact and never travels with a copied overlay.
export function buildOverlayEnvelope(kind, data) {
  const payload = { ...data }
  delete payload.index
  return { _videogen_clip_builder_overlay: true, version: 1, kind, data: payload }
}

export function parseOverlayEnvelope(text) {
  let envelope
  try {
    envelope = JSON.parse(text)
  } catch {
    return null
  }
  if (!envelope || !envelope._videogen_clip_builder_overlay) return null
  return envelope
}
